import '../references/styles.scss';
import './styles.scss';
import React, { useState } from 'react';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Drawer from 'antd/lib/drawer';
import { CheckOutlined, StopOutlined } from '@ant-design/icons';

import { plural, t } from 'utils/i18n';
import LibraryThumb from '../references/library-thumb';
import { previewUrl } from '../references/reference-model';
import type { SceneItem, SceneMode } from './scene-api';
import {
    itemStatus, itemTitle, materialName, noModel, pickerGroups,
} from './scene-model';

interface Props {
    item: SceneItem;
    mode: SceneMode;
    backendAPI: string;
    onApply(value: string, includeExceptions: boolean): Promise<void>;
    onClose(): void;
}

interface Card {
    // a material path, or "" for the fallback
    value: string;
    name: string;
    preview: string | null;
}

function messageOf(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}

// "No model" has no preview: the sign tells it from a preview that did not load
export function NoModelThumb({ className }: { className: string }): JSX.Element {
    return (
        <div className={`${className} cvat-scene-no-model`} role='img' aria-label={t('No model')}>
            <StopOutlined />
        </div>
    );
}

// Picks one material or model for all shapes of a scene item, like the model picker does for one detection
export default function ScenePicker({
    item, mode, backendAPI, onApply, onClose,
}: Props): JSX.Element {
    const [selected, setSelected] = useState<string | null>(null);
    const [includeExceptions, setIncludeExceptions] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const model = item.kind === 'model';
    let emptyName = item.fallback?.name ?? t('The converter fallback');
    if (model) emptyName = t('No model');
    else if (item.id === 'surface') emptyName = t('The material of the furniture model');
    const empty: Card = { value: '', name: emptyName, preview: item.fallback?.preview ?? null };
    const groups = [
        ...pickerGroups(item).map((group) => ({
            title: t(group.title),
            cards: group.materials.map((material) => ({
                value: material.path,
                // library models are named after their file
                name: model ? materialName(material.name) : material.name,
                preview: material.image_path,
            })),
        })),
        // for a model the heading says what no model does: the card is too narrow for it
        { title: model ? noModel(item.id) : t('Fallback'), cards: [empty] },
    ];
    const current = item.value ?? null;
    const canApply = (value: string | null): boolean => (
        !busy && value !== null && (value !== current || includeExceptions)
    );

    const apply = async (value: string): Promise<void> => {
        setBusy(true);
        setError(null);
        try {
            await onApply(value, includeExceptions);
            onClose();
        } catch (e) {
            setError(messageOf(e));
            setBusy(false);
        }
    };

    let now: string | null = null;
    if (itemStatus(item, mode) === 'other' && item.value) {
        const name = materialName(item.value);
        now = model ? t('Now: {name}, not from the library', { name }) : t('Now: {name}, not from the project', { name });
    }
    const title = (
        <div>
            <div className='cvat-references-picker-heading'>
                {t(model ? 'Model for: {item}' : 'Material for: {item}', { item: t(itemTitle(item.id)) })}
            </div>
            <div className='cvat-references-picker-subtitle'>
                {plural(item.count, 'object')}
                {!item.modes.includes(mode) && ` · ${t('{mode} only', { mode: item.modes[0] })}`}
                {now && ` · ${now}`}
            </div>
        </div>
    );

    const footer = (
        <div className='cvat-references-picker-footer'>
            <div>
                {item.exceptions > 0 && (
                    <Checkbox
                        checked={includeExceptions}
                        disabled={busy}
                        onChange={(event) => setIncludeExceptions(event.target.checked)}
                    >
                        {t(model ? 'Also for {n} with another model' : 'Also for {n} with another material', {
                            n: item.exceptions,
                        })}
                    </Checkbox>
                )}
            </div>
            <div>
                <Button onClick={onClose} disabled={busy}>{t('Cancel')}</Button>
                <Button
                    type='primary'
                    disabled={!canApply(selected)}
                    loading={busy}
                    onClick={() => selected !== null && apply(selected)}
                >
                    {t('Apply')}
                </Button>
            </div>
        </div>
    );

    return (
        <Drawer
            className='cvat-references-picker cvat-scene-picker'
            visible
            placement='right'
            width={640}
            title={title}
            footer={footer}
            closable={!busy}
            maskClosable={!busy}
            onClose={onClose}
            getContainer={() => document.querySelector('.cvat-workspace-content') ?? document.body}
            style={{ position: 'absolute' }}
        >
            {error && <Alert className='cvat-scene-picker-error' type='error' showIcon message={error} />}
            {item.candidates.length === 0 && !model && (
                <Alert
                    className='cvat-scene-picker-error'
                    type='info'
                    showIcon
                    message={t('No project materials: General_Materials/{mode} is empty or the project folder is not attached', { mode })}
                />
            )}
            {groups.map((group) => (
                <section key={group.title} className='cvat-scene-picker-group'>
                    <div className='cvat-scene-picker-group-title'>{group.title}</div>
                    <div className='cvat-references-picker-grid'>
                        {group.cards.map((card) => {
                            const classes = ['cvat-references-picker-card'];
                            if (card.value === current) classes.push('cvat-references-picker-card-current');
                            if (card.value === selected) classes.push('cvat-references-picker-card-selected');
                            const choose = (): void => { if (!busy) setSelected(card.value); };
                            return (
                                <div
                                    key={card.value}
                                    className={classes.join(' ')}
                                    role='button'
                                    tabIndex={0}
                                    aria-pressed={card.value === selected}
                                    aria-disabled={busy}
                                    onClick={choose}
                                    onKeyDown={(event) => {
                                        // Enter on the card's button must reach the button
                                        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                                            event.preventDefault();
                                            choose();
                                        }
                                    }}
                                >
                                    {card.value === current && (
                                        <span className='cvat-references-picker-badge'>
                                            <CheckOutlined />
                                            {t('Current material')}
                                        </span>
                                    )}
                                    {model && !card.value ? <NoModelThumb className='cvat-references-picker-preview' /> : (
                                        <LibraryThumb
                                            className='cvat-references-picker-preview'
                                            src={previewUrl(card.preview ?? undefined, backendAPI)}
                                            fullSrc={previewUrl(card.preview ?? undefined, backendAPI, true)}
                                        />
                                    )}
                                    <div className='cvat-references-picker-name' title={card.name} data-testid='material-card-name'>
                                        {card.name}
                                    </div>
                                    {/* one click, like "Use this" of the model picker */}
                                    <Button
                                        size='small'
                                        type='primary'
                                        className='cvat-references-picker-use'
                                        disabled={!canApply(card.value)}
                                        onClick={(event) => { event.stopPropagation(); apply(card.value); }}
                                    >
                                        {t('Apply')}
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                </section>
            ))}
        </Drawer>
    );
}
